import { Injectable } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CreateFundDDQRunRequest } from '@core/models/fund-ddq-run.model';
import { CompanyQuery } from '@core/company/company.query';
import { AppStoreQuery } from '@core/state';
import { filterNil } from '@datorama/akita';
import { FormOption } from '@feature/forms/form-controls/form-option';
import { map, debounceTime, distinctUntilChanged, switchMap, startWith } from 'rxjs/operators';
import { Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { FundDDQRunService, IssuerSearchResult, FundSearchResult } from '@core/fund-ddq-run/fund-ddq-run.service';
import { AssetClassValueObject } from '@core/models/asset-class-value-object';
import { mediumTextMaxLength } from '@feature/forms/wai-validators';

export type FlowType =
  | 'invite_issuer'
  | 'create_fund_later'
  | 'invite_issuer_direct_deal'
  | 'create_direct_deal_later';

export const DIRECT_DEAL_ASSET_CLASS = 'direct-deal';

export function isInviteFlow(flow: FlowType | null | undefined): boolean {
  return flow === 'invite_issuer' || flow === 'invite_issuer_direct_deal';
}

export function isCreateFlow(flow: FlowType | null | undefined): boolean {
  return flow === 'create_fund_later' || flow === 'create_direct_deal_later';
}

export function isDirectDealFlow(flow: FlowType | null | undefined): boolean {
  return flow === 'invite_issuer_direct_deal' || flow === 'create_direct_deal_later';
}

@Injectable({ providedIn: 'root' })
export class CreateFundDialogFormService {
  private readonly firstNameMaxLength = 30;
  private readonly lastNameMaxLength = 50;
  private readonly companyNameMaxLength = 150;

  form: FormGroup = this.buildForm();
  public salutations = this.appStoreQuery
    .selectAvailableSalutations()
    .pipe(map((items) => (items ?? []).map(FormOption.fromValueObject)));

  public emittentCompanies = this.companyQuery.selectAll().pipe(
    filterNil,
    map((companies) => companies.filter((c) => c.isEmittent).map(FormOption.fromCompany)),
  );

  public assetClasses: Observable<FormOption[]> = this.appStoreQuery
    .selectAvailableAssetClasses()
    .pipe(map((items) => items?.map(FormOption.fromValueObject)));

  private selectedAssetClass: BehaviorSubject<AssetClassValueObject['codename']> = new BehaviorSubject<
    AssetClassValueObject['codename']
  >(null);

  public assetSubclasses: Observable<FormOption[]> = this.selectedAssetClass.pipe(
    switchMap((parentCodename) =>
      this.appStoreQuery
        .selectAvailableAssetClasses(parentCodename)
        .pipe(map((items) => items.map(FormOption.fromValueObject))),
    ),
  );

  constructor(
    private readonly companyQuery: CompanyQuery,
    private readonly fundDDQRunService: FundDDQRunService,
    private readonly appStoreQuery: AppStoreQuery,
  ) {}

  public selectAssetClass(assetClass: AssetClassValueObject['codename']): void {
    this.selectedAssetClass.next(assetClass);
  }

  public setNewInvestorFieldsRequired(isRequired: boolean, requireUserType: boolean = true): void {
    const salutationControl = this.form.get('newInvestorSalutation');
    const firstNameControl = this.form.get('newInvestorFirstName');
    const lastNameControl = this.form.get('newInvestorLastName');
    const userTypeControl = this.form.get('newInvestorUserType');
    const companyNameControl = this.form.get('newInvestorCompanyName');

    const salutationValidators = isRequired ? [Validators.required] : [];
    const firstNameValidators = isRequired
      ? [Validators.required, Validators.maxLength(this.firstNameMaxLength)]
      : [Validators.maxLength(this.firstNameMaxLength)];
    const lastNameValidators = isRequired
      ? [Validators.required, Validators.maxLength(this.lastNameMaxLength)]
      : [Validators.maxLength(this.lastNameMaxLength)];
    const companyNameValidators = [Validators.maxLength(this.companyNameMaxLength)];
    salutationControl?.setValidators(salutationValidators);
    firstNameControl?.setValidators(firstNameValidators);
    lastNameControl?.setValidators(lastNameValidators);
    userTypeControl?.setValidators(isRequired && requireUserType ? [Validators.required] : []);
    if (!isRequired) {
      companyNameControl?.setValidators(companyNameValidators);
    } else if (requireUserType && userTypeControl?.value === 'company') {
      companyNameControl?.setValidators([Validators.required, ...companyNameValidators]);
    } else {
      companyNameControl?.setValidators(companyNameValidators);
    }

    salutationControl?.updateValueAndValidity({ emitEvent: false });
    firstNameControl?.updateValueAndValidity({ emitEvent: false });
    lastNameControl?.updateValueAndValidity({ emitEvent: false });
    userTypeControl?.updateValueAndValidity({ emitEvent: false });
    companyNameControl?.updateValueAndValidity({ emitEvent: false });
  }

  public hasValidNewInvestorRequiredFields(requireUserType: boolean = true): boolean {
    const salutation = this.form.get('newInvestorSalutation')?.value;
    const firstName = (this.form.get('newInvestorFirstName')?.value as string | null)?.trim();
    const lastName = (this.form.get('newInvestorLastName')?.value as string | null)?.trim();
    const userType = this.form.get('newInvestorUserType')?.value as 'natural_person' | 'company' | null;
    const companyName = (this.form.get('newInvestorCompanyName')?.value as string | null)?.trim();
    const firstNameControl = this.form.get('newInvestorFirstName');
    const lastNameControl = this.form.get('newInvestorLastName');
    const companyNameControl = this.form.get('newInvestorCompanyName');
    if (firstNameControl?.invalid || lastNameControl?.invalid || companyNameControl?.invalid) {
      return false;
    }

    if (!requireUserType) {
      return !!(salutation && firstName && lastName);
    }
    const hasCompanyNameIfNeeded = userType !== 'company' || !!companyName;
    return !!(salutation && firstName && lastName && userType && hasCompanyNameIfNeeded);
  }

  toModel(data: {
    issuerUid?: string | null;
    issuerName?: string | null;
    fundName: string;
    productUid?: string | null;
    flowType?: FlowType | null;
    fundType?: string | null;
    fundSubtype?: string | null;
    keepPrivate?: boolean | null;
    clientCompany?: string | number | null;
  }): CreateFundDDQRunRequest {
    // Determine which endpoint to use based on issuerUid availability:
    // - If issuerUid exists → use /funds/create-by-issuer/ (existing issuer)
    // - If issuerUid does NOT exist but issuerName exists → use /funds/create/ (new issuer by name)
    // This matches the backend requirements:
    //   - /funds/create-by-issuer/ requires issuer_uid
    //   - /funds/create/ requires issuer_name (and does NOT accept issuer_uid)
    const useCreateByIssuer = !!data.issuerUid;
    const keepPrivate = !!data.keepPrivate;
    const fundType =
      data.fundType ||
      (data.fundSubtype && isDirectDealFlow(data.flowType) ? DIRECT_DEAL_ASSET_CLASS : undefined);

    return {
      issuerUid: data.issuerUid || null,
      issuerName: data.issuerName || null,
      fundName: data.fundName,
      useCreateByIssuer,
      // Include asset class and subclass if provided (form controls store codename strings)
      assetClass: fundType || undefined,
      assetSubclass: data.fundSubtype || undefined,
      keepPrivate,
      ...(keepPrivate && data.clientCompany != null && data.clientCompany !== ''
        ? { clientCompany: String(data.clientCompany) }
        : {}),
    };
  }

  buildForm(): FormGroup {
    const form = new FormGroup({
      flowType: new FormControl<FlowType | null>(null, [Validators.required]), // 'invite_issuer' or 'create_fund_later'
      issuerSearch: new FormControl(''), // Control for autocomplete input
      issuerUid: new FormControl(null),
      issuerCompanyUid: new FormControl(null),
      issuerName: new FormControl(null),
      fundSearch: new FormControl('', [Validators.maxLength(mediumTextMaxLength)]), // Control for fund autocomplete input
      fundName: new FormControl(null, [Validators.maxLength(mediumTextMaxLength)]),
      productUid: new FormControl(null),
      fundType: new FormControl(null),
      fundSubtype: new FormControl(null),
      keepPrivate: new FormControl(false),
      clientCompany: new FormControl<string | number | null>(null),
      issuerAdminEmail: new FormControl('', [Validators.email]),
      /**
       * Controls the "email dropdown vs invite new admin email input" UX.
       * - '' means: "Invite new admin" selected (show email input)
       * - otherwise: selected existing admin email from dropdown
       */
      issuerAdminEmailChoice: new FormControl('', { nonNullable: true }),
      issuerAdminUserUid: new FormControl(null),
      // GP (emittent) specific fields
      // New investor fields (shown when exists === false)
      newInvestorSalutation: new FormControl(''),
      newInvestorFirstName: new FormControl('', [Validators.maxLength(this.firstNameMaxLength)]),
      newInvestorLastName: new FormControl('', [Validators.maxLength(this.lastNameMaxLength)]),
      newInvestorUserType: new FormControl<'natural_person' | 'company' | null>(null),
      newInvestorCompanyName: new FormControl('', [Validators.maxLength(this.companyNameMaxLength)]),
    });

    // Add custom validator: either issuerUid or issuerName must be provided
    form.setValidators([
      (group: FormGroup) => {
        const issuerUid = group.get('issuerUid')?.value;
        const issuerName = group.get('issuerName')?.value;
        if (!issuerUid && !issuerName) {
          return { issuerRequired: true };
        }
        return null;
      },
    ]);

    return form;
  }

  /**
   * Get observable of issuer options based on search input
   * @param searchControl The FormControl for issuer search
   * @returns Observable of IssuerSearchResult[]
   */
  getIssuerOptions$(searchControl: FormControl): Observable<IssuerSearchResult[]> {
    return searchControl.valueChanges.pipe(
      startWith(''),
      debounceTime(300), // Keep search responsive while avoiding request spam
      distinctUntilChanged(),
      switchMap((searchTerm: string | IssuerSearchResult) => {
        // If searchTerm is an object (selected option), don't search - return empty
        // This prevents searching when user has selected an option
        if (searchTerm && typeof searchTerm === 'object' && 'uid' in searchTerm) {
          return of([]);
        }

        // If searchTerm is a string, search for issuers
        if (!searchTerm || typeof searchTerm !== 'string' || searchTerm.trim().length === 0) {
          return of([]);
        }

        return this.fundDDQRunService.searchIssuers(searchTerm);
      }),
    );
  }

  /**
   * Get observable of fund options based on issuer, search input, and optional flow type.
   * Direct Deal flows send asset_class=direct-deal so only Direct Deal funds are returned.
   */
  getFundOptions$(
    issuerUidControl: FormControl,
    fundSearchControl: FormControl,
    flowTypeControl?: FormControl,
  ): Observable<FundSearchResult[]> {
    const flowType$ = flowTypeControl
      ? flowTypeControl.valueChanges.pipe(startWith(flowTypeControl.value))
      : of(null as FlowType | null);

    return combineLatest([
      issuerUidControl.valueChanges.pipe(startWith(issuerUidControl.value)),
      fundSearchControl.valueChanges.pipe(startWith(fundSearchControl.value || '')),
      flowType$,
    ]).pipe(
      debounceTime(300),
      distinctUntilChanged(
        (prev, curr) => prev[0] === curr[0] && prev[1] === curr[1] && prev[2] === curr[2],
      ),
      switchMap(
        ([issuerUid, searchTerm, _flowType]: [string | null, string | FundSearchResult, FlowType | null]) => {
          if (searchTerm && typeof searchTerm === 'object' && 'productUid' in searchTerm) {
            return of([]);
          }

          const query = typeof searchTerm === 'string' ? searchTerm : '';
          if (!query || query.trim().length === 0) {
            return of([]);
          }

          // Always read latest flow type (setValue may have raced with this emission)
          const flowType = (flowTypeControl?.value ?? _flowType) as FlowType | null;

          return this.fundDDQRunService.searchFundsForDropdown({
            issuerUid: issuerUid || undefined,
            query: query.trim(),
            ...(isDirectDealFlow(flowType) ? { assetClass: DIRECT_DEAL_ASSET_CLASS } : {}),
          });
        },
      ),
    );
  }
}
